import React, { Component } from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";

import { Button, Modal, LoadingIndicator } from "../../../../components/simple";

import SearchInput from "../../../../components/complex/SearchInput";
import SelectedVariablesList from "./SelectedVariablesList";
import Category from "./Category/Category";
import Variable from "../../../../components/complex/Variable";

import SVG from "../../../../components/SVG";
import dndIcon from "../../../../assets/images/dnd-icon.svg";

import styles from "./variables-builder.module.scss";

function filterBySearch(variablesSearchTerm) {
  return variable => {
    let isInclude = variable.name
      .toLowerCase()
      .includes(variablesSearchTerm.toLowerCase());
    if (!isInclude && variable.searchTags) {
      isInclude = variable.searchTags.includes(
        variablesSearchTerm.toLowerCase()
      );
    }
    return isInclude;
  };
}

function getSelectedSubcategories(selectedVariables, variables) {
  if (!variables.length || !selectedVariables.length) return [];
  const ids = selectedVariables.flat().map(variable => variable.id);
  const selectedSubcategories = variables
    .filter(variable => ids.includes(variable.id))
    .map(({ subCategory }) => subCategory)
    .filter((subCategory, idx, list) => {
      const beforeValue = list.slice(0, idx);
      return !beforeValue.some(({ id }) => subCategory.id === id);
    });
  return selectedSubcategories;
}

const maxVariableLength = 10;

class VariablesBuilderModal extends Component {
  constructor(props) {
    super(props);
    const selectedSubcategories = getSelectedSubcategories(
      props.selectedVariables,
      props.variables
    );
    this.state = {
      variablesSearchTerm: "",
      selectedSubcategories,
      selectedVariables: [...props.selectedVariables, []],
      searchSubcategories: []
    };
  }

  componentDidMount() {
    const { variables } = this.props;
    if (variables.length) {
      this.recalculateSelected();
    }
  }

  componentDidUpdate(prevProps) {
    const { variables } = this.props;
    if (variables.length !== prevProps.variables.length) {
      this.recalculateSelected();
    }
  }

  handleSubmit = () => {
    const { selectedVariables } = this.state;
    this.props.onSelect(selectedVariables.filter(item => item.length));
    this.props.toggle();
  };

  getUniqueSubcategories = (searchTerm = "") => {
    const { variables, subcategories } = this.props;

    const variablesSearch = variables
      .filter(filterBySearch(searchTerm))
      .reduce((result, current) => {
        return [...result, current.subCategory];
      }, []);

    const subcategoriesSearch = subcategories.filter(
      filterBySearch(searchTerm)
    );

    const searchResult = [...subcategoriesSearch, ...variablesSearch];

    const uniqueSubcategories = searchResult.reduce((result, current) => {
      const found = result.find(item => item.id === current.id);

      return !found ? [...result, current] : result;
    }, []);

    return uniqueSubcategories;
  };

  handleVariablesSearch = (name, value) => {
    const searchSubcategories = this.getUniqueSubcategories(value);

    this.setState({
      variablesSearchTerm: value,
      searchSubcategories
    });
  };

  handleSearchSubcategorySelect = selectedSubcategory => {
    const { searchSubcategories } = this.state;
    if (!searchSubcategories.length) {
      return this.setState({
        searchSubcategories: [selectedSubcategory]
      });
    }
    const newSelectedSubcategories = searchSubcategories.filter(
      subcategory => subcategory.id !== selectedSubcategory.id
    );
    if (searchSubcategories.length !== newSelectedSubcategories.length) {
      return this.setState({
        searchSubcategories: [...newSelectedSubcategories]
      });
    }
    return this.setState({
      searchSubcategories: [...searchSubcategories, selectedSubcategory]
    });
  };

  handleSubcategorySelect = async selectedSubcategory => {
    const { selectedSubcategories } = this.state;
    if (!selectedSubcategories.length) {
      return this.setState({
        selectedSubcategories: [selectedSubcategory]
      });
    }
    const newSelectedSubcategories = selectedSubcategories.filter(
      subcategory => subcategory.id !== selectedSubcategory.id
    );
    if (selectedSubcategories.length !== newSelectedSubcategories.length) {
      return this.setState({
        selectedSubcategories: [...newSelectedSubcategories]
      });
    }
    return this.setState({
      selectedSubcategories: [...selectedSubcategories, selectedSubcategory]
    });
  };

  handleVariableSelect = newVariable => {
    const { selectedVariables: selectedGroups } = this.state;
    const { variables } = this.props;

    if (!selectedGroups.length) {
      return this.setState({
        selectedVariables: [[newVariable], []]
      });
    }

    let flatVariables = selectedGroups.flat().filter(variable => variable.id);
    const isWithoutSubcategory = flatVariables.some(item => !item.subCategory);
    if (isWithoutSubcategory) {
      flatVariables = flatVariables.map(selVariable =>
        variables.find(variable => variable.id === selVariable.id)
      );
    }
    const isSelected = flatVariables.some(variable => {
      return variable.id === newVariable.id;
    });

    const notEmptyGroups = selectedGroups
      .filter(group => group.length)
      .map(group => {
        if (Array.isArray(group)) {
          return group.map(selVariable =>
            variables.find(variable => variable.id === selVariable.id)
          );
        }
        return false;
      });

    if (isSelected) {
      const newSelectedVariables = notEmptyGroups.map(group =>
        group.filter(variable => variable.id !== newVariable.id)
      );
      const filteredNewSelectedVariables = newSelectedVariables
        .filter(group => group.length)
        .map(group => {
          if (Array.isArray(group)) {
            return group.map(selVariable =>
              variables.find(variable => variable.id === selVariable.id)
            );
          }
          return false;
        });

      return this.setState({
        selectedVariables: [...filteredNewSelectedVariables, []]
      });
    }

    if (flatVariables.length >= maxVariableLength) {
      return null;
    }

    const listCategories = flatVariables.map(variable => variable.subCategory);
    const isCategorySelected = listCategories.some(
      // category => category.id === newVariable.subCategory.id
      category =>
        category.id
          ? category.id === newVariable.subCategory.id
          : category === newVariable.subCategory.id
    );

    if (!isCategorySelected) {
      return this.setState({
        selectedVariables: [...notEmptyGroups, [newVariable], []]
      });
    }
    const relatedGroupIndex = notEmptyGroups.findIndex(group => {
      return group[0].subCategory.id === newVariable.subCategory.id;
    });
    const relatedGroup = [...notEmptyGroups[relatedGroupIndex], newVariable];
    return this.setState({
      selectedVariables: [
        ...notEmptyGroups.map((group, index) => {
          if (index === relatedGroupIndex) return relatedGroup;
          return group;
        }),
        []
      ]
    });
  };

  handleRemoveVariable = variableId => {
    // TODO: refactor this

    const { selectedVariables: selectedGroups } = this.state;
    const { variables } = this.props;

    let flatVariables = selectedGroups.flat().filter(variable => variable.id);
    const isWithoutSubcategory = flatVariables.some(item => !item.subCategory);
    if (isWithoutSubcategory) {
      flatVariables = flatVariables.map(selVariable =>
        variables.find(variable => variable.id === selVariable.id)
      );
    }
    const isSelected = flatVariables.some(variable => {
      return variable.id === variableId;
    });

    const notEmptyGroups = selectedGroups
      .filter(group => group.length)
      .map(group => {
        if (Array.isArray(group)) {
          return group.map(selVariable =>
            variables.find(variable => variable.id === selVariable.id)
          );
        }
        return false;
      });

    if (isSelected) {
      const newSelectedVariables = notEmptyGroups.map(group =>
        group.filter(variable => variable && variable?.id !== variableId)
      );
      const filteredNewSelectedVariables = newSelectedVariables
        .filter(group => group.length)
        .map(group => {
          if (Array.isArray(group)) {
            return group.map(selVariable =>
              variables.find(variable => variable.id === selVariable.id)
            );
          }
          return false;
        });

      return this.setState({
        selectedVariables: [...filteredNewSelectedVariables, []]
      });
    }

    if (flatVariables.length >= maxVariableLength) {
      return null;
    }

    return this.setState({
      selectedVariables: [
        ...notEmptyGroups.map(group => {
          return group;
        }),
        []
      ]
    });
  };

  handleRemoveGroup = groupId => {
    const { selectedVariables: selectedGroups } = this.state;

    this.setState({
      selectedVariables: [
        ...selectedGroups.filter(
          (group, index) => groupId !== index && group.length
        ),
        []
      ]
    });
  };

  recalculateSelected() {
    const { selectedVariables, variables } = this.props;
    const selectedSubcategories = getSelectedSubcategories(
      selectedVariables,
      variables
    );

    if (selectedSubcategories.length) {
      this.setState({
        selectedSubcategories
      });
    }
  }

  renderSubcategoriesVariables = subcategoriesForRender => {
    const {
      variablesSearchTerm,
      selectedSubcategories,
      searchSubcategories,
      selectedVariables
    } = this.state;
    const { variables } = this.props;

    const selectedVariablesFlat = selectedVariables.flat();

    // const [first, ...rest] = [...selectedSubcategories].reverse()

    const subcategoriesData = variablesSearchTerm.length
      ? subcategoriesForRender.filter(subCategory =>
          searchSubcategories.includes(subCategory)
        )
      : // : [first, ...rest.reverse()]
        selectedSubcategories;

    return subcategoriesData.map(selectedSubcategory => {
      const variablesList = variables.filter(({ subCategory }) => {
        return subCategory.id === selectedSubcategory.id;
      });

      const filteredVariablesList = variablesList.filter(
        filterBySearch(variablesSearchTerm)
      );

      let variablesDataList;

      if (filteredVariablesList.length) {
        variablesDataList = filteredVariablesList;
      } else {
        variablesDataList = variablesList;
      }

      return (
        <div
          key={`selected-subcategory-${selectedSubcategory.id}`}
          className={styles["subcategory-container"]}
        >
          <div className={styles["subcategory-header"]}>
            {selectedSubcategory.name}
          </div>
          <div className={styles["variables-list"]}>
            {variablesDataList.map(variable => {
              const isVariableSelected = selectedVariablesFlat.some(
                selectedVariable => variable.id === selectedVariable.id
              );

              return (
                <Variable
                  key={`variable-${variable.id}`}
                  active={isVariableSelected}
                  label={variable.name}
                  onClick={() => this.handleVariableSelect(variable)}
                />
              );
            })}
          </div>
        </div>
      );
    });
  };

  render() {
    const {
      variablesSearchTerm,
      selectedSubcategories,
      selectedVariables,
      searchSubcategories
    } = this.state;
    const { categories, isCategoriesLoading } = this.props;

    const { toggle, isOpen, subcategories } = this.props;
    const selectedVariablesFlat = selectedVariables.flat();

    const uniqueSubcategories = this.getUniqueSubcategories(
      variablesSearchTerm
    );

    return (
      <Modal
        className={`screen-modal ${styles["variables-modal"]}`}
        styles={{ maxWidth: 1200 }}
        isOpen={isOpen}
      >
        <div className="d-flex">
          <div className={styles["available-variables-container"]}>
            <div className={styles["categories-selector-container"]}>
              {!isCategoriesLoading &&
              categories.length &&
              subcategories.length ? (
                categories.map(category => {
                  return (
                    <Category
                      category={category}
                      subcategories={uniqueSubcategories.filter(
                        subcategory => subcategory.category.id === category.id
                      )}
                      key={`category-${category.id}`}
                      subcategorySelect={
                        variablesSearchTerm.length
                          ? this.handleSearchSubcategorySelect
                          : this.handleSubcategorySelect
                      }
                      selectedSubcategories={
                        variablesSearchTerm.length
                          ? searchSubcategories
                          : selectedSubcategories
                      }
                    />
                  );
                })
              ) : (
                <div
                  style={{
                    height: "100vh",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center"
                  }}
                >
                  <LoadingIndicator />
                </div>
              )}
            </div>
            <div className={styles["variables-selector-container"]}>
              <div className={styles["search-bar-container"]}>
                <SearchInput
                  value={variablesSearchTerm}
                  handleChange={this.handleVariablesSearch}
                  name="searchVariables"
                />
              </div>
              <div className={styles["subcategories-container"]}>
                {this.renderSubcategoriesVariables(uniqueSubcategories)
                  .length ? (
                  this.renderSubcategoriesVariables(uniqueSubcategories)
                ) : (
                  <div>
                    {variablesSearchTerm ? (
                      "Nothing found"
                    ) : (
                      <span>&#x2190; Select any subcategory</span>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
          <div className={styles["selected-variables-container"]}>
            <div className={styles["selected-variables-title"]}>
              Selected variables
            </div>
            <div className={styles["selected-variables-subtitle"]}>
              <img src={dndIcon} alt="drag and drop" />
              <span>You can move elements</span>
            </div>
            <SelectedVariablesList
              dndVariables={selectedVariables}
              onReorder={reordered => {
                this.setState({
                  selectedVariables: [
                    ...reordered.filter(item => item.length),
                    []
                  ]
                });
              }}
              onRemoveVariable={this.handleRemoveVariable}
              onRemoveGroup={this.handleRemoveGroup}
            />
            <div className={`${styles["variables-selected-footer"]}`}>
              <div>
                <Button
                  onClick={this.handleSubmit}
                  className={`button ${styles["submit-variables-button"]}`}
                >
                  ADD
                </Button>
              </div>
              <div className={styles["variables-selected-counter-wrapper"]}>
                Variables{" "}
                <span className={styles["variables-selected-counter"]}>
                  <span className={styles["variables-selected-count"]}>
                    {selectedVariablesFlat.length}
                  </span>{" "}
                  /{" "}
                  <span
                    className={[
                      selectedVariablesFlat.length >= maxVariableLength
                        ? "danger"
                        : ""
                    ]}
                  >
                    {maxVariableLength}
                  </span>
                </span>
              </div>
            </div>
          </div>
        </div>
        <SVG.Close className={`${styles["close-modal"]}`} onClick={toggle} />
      </Modal>
    );
  }
}

VariablesBuilderModal.propTypes = {
  categories: PropTypes.arrayOf(PropTypes.shape({})),
  subcategories: PropTypes.arrayOf(PropTypes.shape({})),
  variables: PropTypes.arrayOf(PropTypes.shape({})),
  selectedVariables: PropTypes.arrayOf(PropTypes.arrayOf(PropTypes.shape({}))),
  onSelect: PropTypes.func.isRequired,
  toggle: PropTypes.func.isRequired,
  isOpen: PropTypes.bool,
  isCategoriesLoading: PropTypes.bool
};

VariablesBuilderModal.defaultProps = {
  categories: [],
  subcategories: [],
  variables: [],
  selectedVariables: [],
  isOpen: false,
  isCategoriesLoading: false
};

const mapStateToProps = ({ audiences }) => {
  return {
    countries: audiences.countries,
    categories: audiences.categories,
    subcategories: audiences.subcategories,
    variables: audiences.variables
  };
};

export default connect(mapStateToProps, null)(VariablesBuilderModal);
